import { sha256 } from "js-sha256";
import { base64FromBytes } from "./base64";
import { COMMON_CACHE_KEYS } from "./cache";

/** Sets the theme class before the first paint; inlined in the head of `AppHtml` and `Html`, and allowed by its hash below. */
export const THEME_INIT_SCRIPT = `(function(){try{var r=localStorage.getItem(${JSON.stringify(COMMON_CACHE_KEYS.theme)});var t=r?JSON.parse(r).value:"system";var d=t==="dark"||(t!=="light"&&window.matchMedia("(prefers-color-scheme: dark)").matches);var e=document.documentElement;e.classList.toggle("dark",d);e.style.colorScheme=d?"dark":"light";}catch(e){}})();`;

/** The CSP source that allows one inline script, by the SHA-256 of its exact text. */
export const cspHash = (script: string): string => `'sha256-${base64FromBytes(new Uint8Array(sha256.arrayBuffer(script)))}'`;

// Sign in with Apple loads its script (and any style it brings) from Apple's CDN and signs in through a popup on appleid.apple.com.
const APPLE_SCRIPT = "https://appleid.cdn-apple.com";
const APPLE_ID = "https://appleid.apple.com";

export const CLOUDFLARE_WEB_ANALYTICS: Required<Pick<CspSources, "script" | "connect">> = {
  script: ["https://static.cloudflareinsights.com"],
  connect: ["https://cloudflareinsights.com"],
};

/** What a page loads beyond its own origin, per directive. Every list adds to the framework's defaults. */
export type CspSources = {
  script?: string[];
  connect?: string[];
  img?: string[];
  media?: string[];
  frame?: string[];
  formAction?: string[];
  /** Stylesheet and font hosts, for a page that still shows third-party fonts (a site builder previewing its customers' fonts). */
  style?: string[];
  font?: string[];
  /** Who may frame the page; `'none'` unless given (`'self'` for a page that frames its own previews). */
  frameAncestors?: string[];
};

/**
 * The Content-Security-Policy of a framework page. Scripts come from the page's origin only (plus the theme script by its hash,
 * Apple's sign-in script and `sources.script`), page data is JSON that never runs, plugins and `<base>` are off, forms post
 * to the origin, and no other site may frame the page. Styles allow inline (`style` attributes are everywhere) and images
 * any https source (the catalogue and author pictures come from many hosts); fonts are self-hosted.
 */
export const contentSecurityPolicy = (sources: CspSources = {}): string =>
  [
    ["default-src", "'self'"],
    ["script-src", "'self'", cspHash(THEME_INIT_SCRIPT), APPLE_SCRIPT, ...(sources.script ?? [])],
    ["style-src", "'self'", "'unsafe-inline'", APPLE_SCRIPT, ...(sources.style ?? [])],
    ["img-src", "'self'", "data:", "blob:", "https:", ...(sources.img ?? [])],
    ["font-src", "'self'", APPLE_SCRIPT, ...(sources.font ?? [])],
    ["connect-src", "'self'", APPLE_ID, ...(sources.connect ?? [])],
    ["media-src", "'self'", "blob:", ...(sources.media ?? [])],
    ["frame-src", APPLE_ID, ...(sources.frame ?? [])],
    ["worker-src", "'self'", "blob:"],
    ["manifest-src", "'self'"],
    ["object-src", "'none'"],
    ["base-uri", "'self'"],
    ["form-action", "'self'", ...(sources.formAction ?? [])],
    ["frame-ancestors", ...(sources.frameAncestors ?? ["'none'"])],
  ]
    .map((directive) => directive.join(" "))
    .join("; ");

/**
 * For an API origin: its JSON is never a page, and the odd page it answers (a confirmation after a link in an email) runs no
 * script, styles itself inline, posts its forms back to the API and cannot be framed.
 */
export const API_CONTENT_SECURITY_POLICY =
  "default-src 'none'; style-src 'unsafe-inline'; img-src 'self' data:; form-action 'self'; base-uri 'none'; frame-ancestors 'none'";
